import { API_BASE_URL } from '../config/api';

const API_BASE = API_BASE_URL;

// =============================================================================
// HTTP Utilities
// =============================================================================

/**
 * Fetch with credentials (cookies) included
 */
export function fetchWithCredentials(url: string, options: RequestInit = {}): Promise<Response> {
  return fetch(url, {
    ...options,
    credentials: 'include',
  });
}

/**
 * Refresh access token using refresh token cookie.
 * Returns success flag and optional error reason (e.g., 'Account is paused').
 * `isUnreachable` means the server never answered, so the session may still be valid.
 */
interface RefreshResult {
  success: boolean;
  reason?: string;
  isUnreachable?: boolean;
}

export async function refreshAccessToken(): Promise<RefreshResult> {
  try {
    const res = await fetchWithCredentials(`${API_BASE}/api/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    });

    if (res.ok) return { success: true };
    const data = await res.json().catch(() => ({}));
    return { success: false, reason: data.error };
  } catch {
    return { success: false, isUnreachable: true };
  }
}

type SessionEndedHandler = (reason?: string) => void;

let onSessionEnded: SessionEndedHandler | null = null;

/** Registers what runs when the server refuses a token refresh; AuthProvider signs the user out. */
export function setSessionEndedHandler(handler: SessionEndedHandler | null): void {
  onSessionEnded = handler;
}

let refreshInFlight: Promise<RefreshResult> | null = null;

/** Requests that 401 together share one refresh, so a burst refreshes, or ends the session, once. */
function refreshSession(): Promise<RefreshResult> {
  refreshInFlight ??= refreshAccessToken()
    .then((result) => {
      if (!result.success && !result.isUnreachable) onSessionEnded?.(result.reason);
      return result;
    })
    .finally(() => {
      refreshInFlight = null;
    });
  return refreshInFlight;
}

/**
 * Auth-aware fetch: sends cookies, auto-refreshes token on 401, then retries.
 * Safe to use in stores and hooks that can't access AuthContext directly.
 */
export async function authAwareFetch(url: string, init?: RequestInit): Promise<Response> {
  let res = await fetchWithCredentials(url, init);
  if (res.status === 401) {
    const refreshResult = await refreshSession();
    if (refreshResult.success) {
      res = await fetchWithCredentials(url, init);
    }
  }
  return res;
}
